// src/utils/emailTemplateLoader.js
// Firestore I/O for the emailTemplates collection. One doc per template id.
// A missing doc → caller falls back to the hardcoded DEFAULT_TEMPLATES.

import {
  collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { TRANSACTION_TEMPLATE_META } from "./transactionEmailSequence";
import {
  cleanPastedText,
  cleanPastedSubject,
} from "./emailBrandTemplate";
import { normalizeGreetings } from "./emailPlaceholders";

const BASE_TEMPLATE_IDS = [
  // Open House (auto-triggered by OH flow)
  "scheduling-seller",
  "scheduling-agent",
  "update-seller",
  "update-agent",
  "cancellation-seller",
  "cancellation-agent",
  "recap-seller",
  // Client touchpoints (manual send via SendTemplateModal)
  "birthday",
  "home-anniversary",
  "closing-day",
  "welcome",
  // Marketing (manual send)
  "just-listed",
  "just-sold",
  // Vendor notifications (manual send, fired from deal detail Notify Vendor buttons)
  "staging-consult-vendor",
  "staging-install-vendor",
  "staging-destage-vendor",
  "photographer-vendor",
  "pre-inspection-vendor",
  // Post-Acceptance (manual send from Deal Detail)
  "seller-client-accepted",
  "buyer-client-accepted",
  "title-new-transaction",
];

// Lightweight metadata for the picker / send modal. Keeps category + label
// in one place so the picker doesn't need its own static list.
const BASE_TEMPLATE_META = {
  "scheduling-seller":  { category: "Open House", label: "Schedule · Seller",        meta: "Sent when an OH is scheduled", auto: true },
  "scheduling-agent":   { category: "Open House", label: "Schedule · Hosting Agent", meta: "Invitation to host",           auto: true },
  "update-seller":      { category: "Open House", label: "Update · Seller",          meta: "Time changed",                 auto: true },
  "update-agent":       { category: "Open House", label: "Update · Hosting Agent",   meta: "Reschedule notice",            auto: true },
  "cancellation-seller":{ category: "Open House", label: "Cancel · Seller",          meta: "OH cancelled",                 auto: true },
  "cancellation-agent": { category: "Open House", label: "Cancel · Hosting Agent",   meta: "OH cancelled",                 auto: true },
  "recap-seller":       { category: "Open House", label: "Recap · Seller",           meta: "Post-OH summary",              auto: true },

  "birthday":           { category: "Client Touchpoints", label: "Birthday",         meta: "Wish a client happy birthday" },
  "home-anniversary":   { category: "Client Touchpoints", label: "Home Anniversary", meta: "Congrats on N years in their home" },
  "closing-day":        { category: "Client Touchpoints", label: "Closing Day",      meta: "Day-of-closing greeting" },
  "welcome":            { category: "Client Touchpoints", label: "Welcome",          meta: "First touch for a new client" },

  "just-listed":        { category: "Marketing", label: "Just Listed", meta: "Announce a new listing" },
  "just-sold":          { category: "Marketing", label: "Just Sold",   meta: "Announce a recent close" },

  "staging-consult-vendor": { category: "Vendor Notifications", label: "Staging · Consult Request", meta: "Notify stager for initial consult" },
  "staging-install-vendor": { category: "Vendor Notifications", label: "Staging · Install Confirm", meta: "Confirm install date with stager" },
  "staging-destage-vendor": { category: "Vendor Notifications", label: "Staging · Destage Confirm", meta: "Confirm destage date with stager" },
  "photographer-vendor":    { category: "Vendor Notifications", label: "Photographer · Shoot Confirm", meta: "Confirm photoshoot date with photographer" },
  "pre-inspection-vendor":  { category: "Vendor Notifications", label: "Pre-Listing Inspection", meta: "Request a pre-listing inspection from an inspector" },

  "seller-client-accepted": { category: "Post-Acceptance", label: "Seller Client — Offer Accepted", meta: "Sent to sellers when an offer is accepted", auto: false },
  "buyer-client-accepted":  { category: "Post-Acceptance", label: "Buyer Client — Offer Accepted",  meta: "Sent to buyers when their offer is accepted", auto: false },
  "title-new-transaction":  { category: "Post-Acceptance", label: "Title — New Transaction",        meta: "Sent to our title closer when a deal goes Under Contract", auto: false },
};

// Fold the transaction-sequence emails into the shared registry so they're
// editable in Settings → Email Templates. Base entries win on id conflicts
// (e.g. "title-new-transaction" keeps its Post-Acceptance category/meta).
export const TEMPLATE_META = { ...TRANSACTION_TEMPLATE_META, ...BASE_TEMPLATE_META };
export const TEMPLATE_IDS = [
  ...BASE_TEMPLATE_IDS,
  ...Object.keys(TRANSACTION_TEMPLATE_META).filter(
    (id) => !BASE_TEMPLATE_IDS.includes(id),
  ),
];

// ---------------------------------------------------------------------------
// Custom templates — user-created, under any category, stored in the same
// emailTemplates collection but marked { custom: true } and carrying their own
// label/category/subject/body (no hardcoded default behind them).
// ---------------------------------------------------------------------------

export function isCustomTemplateId(id) {
  return typeof id === "string" && id.startsWith("custom-");
}

export function newCustomTemplateId() {
  return `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Pull custom entries out of the loaded emailTemplates map into picker-shaped
// meta, filling sensible defaults for a freshly-created blank template.
export function customTemplatesFromMap(emailTemplates) {
  const ids = [];
  const meta = {};
  for (const [id, v] of Object.entries(emailTemplates || {})) {
    if (v && v.custom) {
      ids.push(id);
      meta[id] = {
        label: v.label || "Untitled Template",
        category: (v.category || "Custom").trim() || "Custom",
        meta: v.meta || "Custom template",
        custom: true,
        auto: false,
      };
    }
  }
  return { ids, meta };
}

// Who a custom template is addressed to. "client" prefills everyone on the
// deal; the other roles start blank for the agent to fill in (mirrors the
// built-in sequence's recipientRole values). Hardcoding every custom template
// as "client" is what pulled a client into an agent-directed email (Henry,
// 2026-09-01 screenshot).
export const RECIPIENT_ROLE_OPTIONS = [
  { value: "client", label: "Client(s) on the deal" },
  { value: "otherAgent", label: "Other Agent" },
  { value: "lender", label: "Lender" },
  { value: "title", label: "Title" },
  { value: "vendor", label: "Vendor" },
];

const ROLE_LABELS = {
  client: "Client",
  otherAgent: "Other Agent",
  lender: "Lender",
  title: "Title",
  vendor: "Vendor",
};

// Custom templates shaped for the deal's Email Center: cleaned text, the
// recipient role (default: clients), and whether the template is a tracked
// checklist item on deals ("Track on deals" switch — Henry, 2026-09-01:
// custom emails should get "marked off as an item sent").
export function dealCustomTemplates(emailTemplates) {
  return Object.entries(emailTemplates || {})
    .filter(([, v]) => v && v.custom)
    .map(([id, v]) => {
      const role = ROLE_LABELS[v.recipientRole] ? v.recipientRole : "client";
      return {
        id,
        label: v.label || "Untitled Template",
        category: (v.category || "Custom").trim() || "Custom",
        // normalizeGreetings: a literal "Good Morning/Afternoon" typed into a
        // template becomes the time-aware {{greeting}} token — including in
        // templates saved before this existed.
        subject: normalizeGreetings(cleanPastedSubject(v.subject || "")),
        body: normalizeGreetings(cleanPastedText(v.body || "")),
        video: v.video || null,
        recipientRole: role,
        recipientLabel: ROLE_LABELS[role],
        trackOnDeals: v.trackOnDeals === true,
      };
    });
}

// Built-in category order + any new categories introduced by custom templates,
// appended after the base order in alphabetical order, deduped.
export function mergedCategoryList(baseOrder, emailTemplates) {
  const extras = [];
  for (const v of Object.values(emailTemplates || {})) {
    if (v && v.custom) {
      const c = (v.category || "Custom").trim() || "Custom";
      if (!baseOrder.includes(c) && !extras.includes(c)) extras.push(c);
    }
  }
  extras.sort((a, b) => a.localeCompare(b));
  return [...baseOrder, ...extras];
}

/**
 * Read all template customizations from Firestore. Returns a map keyed by
 * template id. Missing templates simply don't appear in the map (caller
 * falls back to DEFAULT_TEMPLATES).
 *
 * Shape per entry:
 *   non-recap: { subject?, body?, updatedAt, updatedBy }
 *   recap:     { subject?, intro?, closing?, updatedAt, updatedBy }
 */
export async function loadEmailTemplates() {
  try {
    const snap = await getDocs(collection(firestored, "emailTemplates"));
    const map = {};
    snap.forEach((d) => {
      const id = d.id;
      const data = d.data();
      // Built-in overrides (known id) OR user-created custom templates.
      if (TEMPLATE_IDS.includes(id) || data?.custom) map[id] = data;
    });
    return map;
  } catch (err) {
    console.error("loadEmailTemplates failed:", err);
    return {};
  }
}

/**
 * Save (upsert) a customization for a single template. Works for built-in
 * template ids and for custom (user-created) ids.
 */
export async function saveEmailTemplate(templateId, fields, userId) {
  if (!TEMPLATE_IDS.includes(templateId) && !isCustomTemplateId(templateId)) {
    throw new Error(`Unknown template id: ${templateId}`);
  }
  const ref = doc(firestored, "emailTemplates", templateId);
  await setDoc(ref, {
    ...fields,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }, { merge: true });
}

/**
 * Create a brand-new blank custom template under any category. Returns the id.
 */
export async function createCustomTemplate(
  {
    label = "Untitled Template",
    category = "Custom",
    subject = "",
    body = "",
    recipientRole = "client",
    trackOnDeals = false,
  } = {},
  userId,
) {
  const id = newCustomTemplateId();
  const ref = doc(firestored, "emailTemplates", id);
  await setDoc(ref, {
    custom: true,
    label,
    category,
    subject,
    body,
    recipientRole,
    trackOnDeals,
    createdBy: userId || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: userId || "",
  });
  return id;
}

/**
 * Delete a template. For a built-in id this reverts to default; for a custom
 * id this removes it entirely. No-op if no doc exists.
 */
export async function deleteEmailTemplate(templateId) {
  if (!TEMPLATE_IDS.includes(templateId) && !isCustomTemplateId(templateId)) {
    throw new Error(`Unknown template id: ${templateId}`);
  }
  const ref = doc(firestored, "emailTemplates", templateId);
  await deleteDoc(ref);
}
